/**
 * Bloqueos manuales — admin panel to manage `user_overrides`.
 *
 * Lets an admin lock specific fields of any user (puesto, función, desc_rol,
 * manager_email, manager_nombre…) so that no future CSV import overrides them.
 *
 * Supported field types:
 *   - text (puesto, funcion, desc_rol, manager_nombre)
 *   - user-email (manager_email — resolves to manager_id automatically server side, or directly here)
 */
import { useEffect, useState } from 'react';
import { userOverridesApi } from '../utils/api';
import { Lock, Plus, Trash2, Save, X, AlertCircle, CheckCircle2, Loader, Info, Edit, ShieldCheck, Download, Upload } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Allowed lockable fields (display-friendly)
const LOCKABLE_FIELDS = [
  { key: 'puesto',         label: 'Puesto', placeholder: 'Ej: Director de Innovación + Responsable Area Plataformas' },
  { key: 'funcion',        label: 'Función / Área', placeholder: 'Ej: F17 INNOVACION' },
  { key: 'manager_email',  label: 'Email del manager (N+1)', placeholder: 'Ej: dredondo@valorizasm.com', type: 'manager' },
  { key: 'manager_nombre', label: 'Nombre del manager', placeholder: 'Auto-rellenado al cambiar email' },
];

export default function UserOverridesAdmin() {
  const [overrides, setOverrides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ email: '', fields: {} });
  const [saving, setSaving] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importText, setImportText] = useState('');
  const [importReplace, setImportReplace] = useState(true);
  const [importBusy, setImportBusy] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await userOverridesApi.list();
      setOverrides(res.data || []);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Error cargando los bloqueos');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditing(null);
    setForm({ email: '', fields: {} });
    setError('');
    setOpenDialog(true);
  };
  const openEdit = (ov) => {
    setEditing(ov);
    setForm({ email: ov.user?.email || '', fields: { ...(ov.fields || {}) } });
    setError('');
    setOpenDialog(true);
  };

  const handleSave = async () => {
    setError(''); setSuccess('');
    if (!form.email.trim()) { setError('Falta el email del usuario'); return; }
    // Strip empty values
    const clean = Object.fromEntries(
      Object.entries(form.fields).filter(([, v]) => v != null && String(v).trim() !== '')
    );
    setSaving(true);
    try {
      // If manager_email is provided, resolve to manager_id from /api/users (server already accepts manager_id directly)
      if (clean.manager_email) {
        try {
          const lookup = await axios.get(
            `${API_URL}/api/users?search=${encodeURIComponent(clean.manager_email)}`,
            { headers: { Authorization: `Bearer ${localStorage.getItem('valoriza_token')}` } }
          );
          const match = (lookup.data || []).find(u => (u.email || '').toLowerCase() === clean.manager_email.toLowerCase());
          if (match) {
            clean.manager_id = match.id;
            if (!clean.manager_nombre) clean.manager_nombre = `${match.first_name || ''} ${match.last_name || ''}`.trim();
          }
        } catch (e) { /* server will still upsert; manager_id will be left missing if unresolved */ }
      }
      await userOverridesApi.upsert(form.email.trim().toLowerCase(), clean);
      setSuccess(editing ? 'Bloqueo actualizado' : 'Bloqueo creado');
      setOpenDialog(false);
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Error guardando');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (email) => {
    if (!window.confirm(`¿Eliminar los bloqueos de ${email}?`)) return;
    try {
      await userOverridesApi.remove(email);
      setSuccess('Bloqueo eliminado');
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Error eliminando');
    }
  };

  const handleExport = async () => {
    setError(''); setSuccess('');
    try {
      const res = await userOverridesApi.exportAll();
      const data = res.data;
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
      a.href = url;
      a.download = `bloqueos-manuales-${stamp}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setSuccess(`Exportados ${data.count} bloqueos. Sube este archivo en producción → "Importar JSON".`);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Error exportando');
    }
  };

  const handleImportFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = (ev) => setImportText(String(ev.target.result || ''));
    reader.readAsText(f);
  };

  const handleImportSubmit = async () => {
    setImportBusy(true); setImportResult(null); setError('');
    try {
      let payload;
      try { payload = JSON.parse(importText); }
      catch { setError('JSON inválido: revisa el formato.'); setImportBusy(false); return; }
      const items = Array.isArray(payload) ? payload : (payload.items || []);
      if (!items.length) { setError('El archivo no contiene `items`.'); setImportBusy(false); return; }
      const res = await userOverridesApi.importMany(items, importReplace);
      setImportResult(res.data);
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Error al importar');
    } finally {
      setImportBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="user-overrides-admin">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold font-heading flex items-center gap-2">
            <ShieldCheck className="h-6 w-6 text-brand" />
            Bloqueos manuales
          </h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Fija manualmente campos de una persona (puesto, función, manager…) para que
            <strong> ningún CSV de RRHH los sobrescriba </strong>
            en las próximas sincronizaciones diarias.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button onClick={handleExport} variant="outline" data-testid="btn-export-overrides" className="gap-2">
            <Download className="h-4 w-4" /> Exportar JSON
          </Button>
          <Button onClick={() => { setImportOpen(true); setImportText(''); setImportResult(null); setError(''); }} variant="outline" data-testid="btn-open-import" className="gap-2">
            <Upload className="h-4 w-4" /> Importar JSON
          </Button>
          <Button onClick={openNew} data-testid="btn-new-override-user" className="gap-2">
            <Plus className="h-4 w-4" /> Nuevo bloqueo
          </Button>
        </div>
      </div>

      {/* Help block about export/import workflow */}
      <div className="rounded-xl border border-amber-200 bg-amber-50 dark:border-amber-900/40 dark:bg-amber-900/10 p-4 flex gap-3 text-sm">
        <Info className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
        <div className="text-amber-900 dark:text-amber-200">
          <p className="font-medium">¿Llevar bloqueos a producción?</p>
          <p className="text-amber-800 dark:text-amber-300 mt-1">
            Pulsa <strong>Exportar JSON</strong> aquí, entra en <code className="bg-amber-100 dark:bg-amber-900/40 px-1 rounded">mytime.valorizasm.com</code>
            con tu cuenta admin, ve a <strong>Configuración → Bloqueos manuales</strong> y pulsa <strong>Importar JSON</strong>.
          </p>
        </div>
      </div>

      <div className="rounded-xl border border-blue-200 bg-blue-50 dark:border-blue-900/40 dark:bg-blue-900/10 p-4 flex gap-3 text-sm">
        <Info className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
        <div className="text-blue-900 dark:text-blue-200">
          <p className="font-medium">¿Cómo funciona?</p>
          <ul className="list-disc list-inside text-blue-800 dark:text-blue-300 mt-1 space-y-0.5">
            <li>Cada bloqueo se aplica <strong>al instante</strong> a la persona.</li>
            <li>Los campos bloqueados <strong>se ignoran en el sync diario</strong> de Drive / SFTP y en la importación masiva.</li>
            <li>Para cambiar de manager, basta poner el <strong>email del N+1</strong>. El sistema resuelve el ID.</li>
          </ul>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive flex items-start gap-2" data-testid="user-override-error">
          <AlertCircle className="h-4 w-4 mt-0.5" /> {error}
        </div>
      )}
      {success && (
        <div className="rounded-lg border border-green-200 bg-green-50 dark:border-green-900/30 dark:bg-green-900/10 p-3 text-sm text-green-700 dark:text-green-300 flex items-start gap-2" data-testid="user-override-success">
          <CheckCircle2 className="h-4 w-4 mt-0.5" /> {success}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12 text-muted-foreground">
          <Loader className="h-5 w-5 animate-spin mr-2" /> Cargando…
        </div>
      ) : overrides.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border p-10 text-center text-sm text-muted-foreground" data-testid="user-overrides-empty">
          <Lock className="h-10 w-10 mx-auto mb-3 opacity-30" />
          <p>No hay bloqueos configurados.</p>
          <p className="mt-1">Pulsa "Nuevo bloqueo" para crear el primero.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {overrides.map((ov) => (
            <div
              key={ov.user_id}
              className="rounded-xl border border-border bg-card p-4 shadow-sm"
              data-testid={`user-override-row-${ov.user?.email}`}
            >
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold">{ov.user?.first_name} {ov.user?.last_name}</span>
                    <span className="text-xs text-muted-foreground">{ov.user?.email}</span>
                  </div>
                  <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-xs">
                    {Object.entries(ov.fields || {}).filter(([k]) => k !== 'manager_id').map(([k, v]) => (
                      <div key={k} className="flex flex-col">
                        <span className="text-[10px] uppercase font-semibold text-muted-foreground tracking-wider">{
                          LOCKABLE_FIELDS.find(f => f.key === k)?.label || k
                        }</span>
                        <span className="font-mono text-foreground break-words">🔒 {String(v)}</span>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    Actualizado {ov.updated_at ? new Date(ov.updated_at).toLocaleString('es-ES') : '—'}
                    {ov.updated_by && <> · por <span className="font-medium">{ov.updated_by}</span></>}
                  </p>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <Button size="sm" variant="outline" onClick={() => openEdit(ov)} data-testid={`btn-edit-uo-${ov.user?.email}`}>
                    <Edit className="h-3.5 w-3.5 mr-1" /> Editar
                  </Button>
                  <Button size="sm" variant="ghost" className="text-destructive hover:bg-destructive/10" onClick={() => handleDelete(ov.user?.email)} data-testid={`btn-delete-uo-${ov.user?.email}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={openDialog} onOpenChange={setOpenDialog}>
        <DialogContent className="max-w-lg" data-testid="user-override-dialog">          <DialogHeader>
            <DialogTitle>{editing ? 'Editar bloqueo' : 'Nuevo bloqueo'}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 pt-2 max-h-[60vh] overflow-y-auto pr-2">
            <div>
              <Label htmlFor="uo-email">Email del usuario</Label>
              <Input
                id="uo-email"
                placeholder="nombre@valorizasm.com"
                value={form.email}
                disabled={!!editing}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="uo-email-input"
                className="mt-1"
              />
              {editing && <p className="text-xs text-muted-foreground mt-1">Para cambiar el usuario, borra y crea un bloqueo nuevo.</p>}
            </div>

            {LOCKABLE_FIELDS.map((f) => (
              <div key={f.key}>
                <Label htmlFor={`uo-${f.key}`}>{f.label} <span className="text-muted-foreground text-xs">(opcional)</span></Label>
                <Input
                  id={`uo-${f.key}`}
                  placeholder={f.placeholder}
                  value={form.fields[f.key] || ''}
                  onChange={(e) => setForm({ ...form, fields: { ...form.fields, [f.key]: e.target.value } })}
                  data-testid={`uo-field-${f.key}`}
                  className="mt-1"
                />
              </div>
            ))}

            <p className="text-xs text-muted-foreground">
              Deja un campo vacío para no bloquearlo. Si dejas todo en blanco, equivale a eliminar el bloqueo.
            </p>

            {error && (
              <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-2.5 text-xs text-destructive flex items-start gap-2">
                <AlertCircle className="h-3.5 w-3.5 mt-0.5" /> {error}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpenDialog(false)} disabled={saving} data-testid="uo-cancel">Cancelar</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="uo-save" className="gap-2">
              {saving ? <Loader className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import dialog */}
      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="max-w-2xl" data-testid="import-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Upload className="h-5 w-5 text-brand" /> Importar bloqueos desde JSON</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 pt-2 max-h-[65vh] overflow-y-auto pr-2">
            <p className="text-sm text-muted-foreground">
              Pega aquí el JSON exportado desde otro entorno o sube el archivo. Los bloqueos se aplicarán al instante
              en esta base de datos, y los campos se protegerán de futuras sincronizaciones CSV.
            </p>

            <div className="flex items-center gap-3 flex-wrap">
              <label className="inline-flex items-center gap-2 cursor-pointer rounded-md border border-border px-3 py-1.5 text-sm hover:bg-muted">
                <Upload className="h-3.5 w-3.5" />
                Cargar archivo .json
                <input
                  type="file"
                  accept="application/json,.json"
                  className="hidden"
                  onChange={handleImportFile}
                  data-testid="import-file-input"
                />
              </label>
              <label className="inline-flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={importReplace}
                  onChange={(e) => setImportReplace(e.target.checked)}
                  data-testid="import-replace"
                />
                Sobrescribir bloqueos existentes
              </label>
            </div>

            <textarea
              value={importText}
              onChange={(e) => setImportText(e.target.value)}
              placeholder='{"items":[{"user_email":"dredondo@valorizasm.com","fields":{"puesto":"..."}}]}'
              data-testid="import-textarea"
              className="w-full h-56 rounded-md border border-border bg-background p-3 font-mono text-xs focus:outline-none focus:ring-2 focus:ring-brand/30"
            />

            {error && (
              <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-2.5 text-xs text-destructive flex items-start gap-2">
                <AlertCircle className="h-3.5 w-3.5 mt-0.5" /> {error}
              </div>
            )}
            {importResult && (
              <div className="rounded-lg border border-green-200 bg-green-50 dark:border-green-900/30 dark:bg-green-900/10 p-3 text-sm space-y-1" data-testid="import-result">
                <div className="flex items-center gap-2 font-medium text-green-700 dark:text-green-300">
                  <CheckCircle2 className="h-4 w-4" />
                  Aplicados: {importResult.applied_count} · Saltados: {importResult.skipped_count} · Errores: {importResult.error_count}
                </div>
                {!!importResult.errors?.length && (
                  <details className="text-xs">
                    <summary className="cursor-pointer text-destructive">Ver errores ({importResult.errors.length})</summary>
                    <ul className="list-disc list-inside mt-1">
                      {importResult.errors.map((e, i) => (
                        <li key={i}><code>{e.email}</code> — {e.error}</li>
                      ))}
                    </ul>
                  </details>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setImportOpen(false)} disabled={importBusy} data-testid="import-close">Cerrar</Button>
            <Button onClick={handleImportSubmit} disabled={importBusy || !importText.trim()} data-testid="import-submit" className="gap-2">
              {importBusy ? <Loader className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              Importar y aplicar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
